// Slider horizontal des sections dx : défilement natif (doigt, trackpad), glisser à la souris,
// flèches, points et barre de progression optionnels, onglets optionnels.
// Repères : [data-dx-track] (la piste active), [data-dx-prev] / [data-dx-next], [data-dx-dots], [data-dx-thumb],
// onglets : [data-dx-tab] (boutons) et [data-dx-panel] (panneaux contenant chacun une piste .dx-track).
if (!customElements.get('dx-slider')) {
  customElements.define(
    'dx-slider',
    class DxSlider extends HTMLElement {
      connectedCallback() {
        this.prevButton = this.querySelector('[data-dx-prev]');
        this.nextButton = this.querySelector('[data-dx-next]');
        this.dots = this.querySelector('[data-dx-dots]');
        this.thumb = this.querySelector('[data-dx-thumb]');
        this.positions = [0];
        this.onScroll = () => this.update();
        this.resizeObserver = new ResizeObserver(() => this.rebuild());

        this.prevButton?.addEventListener('click', () => this.go(-1));
        this.nextButton?.addEventListener('click', () => this.go(1));
        this.initMouseDrag();
        this.initTabs();
        this.setTrack(this.querySelector('[data-dx-track]'));

        // Customizer : un bloc sélectionné dans l'éditeur est amené à l'écran.
        this.addEventListener('shopify:block:select', (event) => {
          const tabIndex = this.tabs.indexOf(event.target.closest('[data-dx-tab]'));
          if (tabIndex >= 0) this.selectTab(tabIndex);
          const item = event.target.closest('[data-dx-track] > *');
          const first = this.track?.children[0];
          if (item && first) this.track.scrollTo({ left: item.offsetLeft - first.offsetLeft, behavior: 'smooth' });
        });
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
      }

      setTrack(track) {
        if (!track || track === this.track) return;
        if (this.track) {
          this.track.removeEventListener('scroll', this.onScroll);
          this.resizeObserver.unobserve(this.track);
        }
        this.track = track;
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.resizeObserver.observe(this.track);
        this.rebuild();
      }

      initTabs() {
        this.tabs = Array.from(this.querySelectorAll('[data-dx-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-dx-panel]'));
        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.selectTab(index));
          tab.addEventListener('keydown', (event) => {
            const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
            if (!step) return;
            event.preventDefault();
            const next = (index + step + this.tabs.length) % this.tabs.length;
            this.selectTab(next);
            this.tabs[next].focus();
          });
        });
      }

      selectTab(index) {
        this.tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', String(i === index));
          tab.tabIndex = i === index ? 0 : -1;
        });
        this.panels.forEach((panel, i) => {
          panel.hidden = i !== index;
          panel.querySelector('.dx-track')?.toggleAttribute('data-dx-track', i === index);
        });
        const track = this.panels[index]?.querySelector('.dx-track');
        if (track) {
          track.scrollLeft = 0;
          this.setTrack(track);
        }
      }

      // Une position d'arrêt par élément, fusionnées en fin de piste.
      rebuild() {
        if (!this.track) return;
        const items = Array.from(this.track.children);
        const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const first = items[0]?.offsetLeft ?? 0;
        this.positions = items
          .map((item) => Math.min(item.offsetLeft - first, max))
          .filter((position, index, all) => index === 0 || position - all[index - 1] > 2);
        this.toggleAttribute('data-scrollable', max > 2);

        if (this.dots) {
          const label = this.dots.dataset.label || '';
          this.dots.replaceChildren(
            ...this.positions.map((position, index) => {
              const dot = document.createElement('button');
              dot.type = 'button';
              dot.className = 'dx-dot';
              dot.setAttribute('aria-label', `${label} ${index + 1}`.trim());
              dot.addEventListener('click', () => this.track.scrollTo({ left: position, behavior: 'smooth' }));
              return dot;
            })
          );
        }
        this.update();
      }

      currentIndex() {
        const left = this.track.scrollLeft;
        return this.positions.reduce(
          (best, position, index) =>
            Math.abs(position - left) < Math.abs(this.positions[best] - left) ? index : best,
          0
        );
      }

      go(direction) {
        if (!this.track) return;
        const index = Math.max(0, Math.min(this.positions.length - 1, this.currentIndex() + direction));
        this.track.scrollTo({ left: this.positions[index], behavior: 'smooth' });
      }

      update() {
        if (!this.track) return;
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const max = scrollWidth - clientWidth;
        if (this.prevButton) this.prevButton.disabled = scrollLeft <= 1;
        if (this.nextButton) this.nextButton.disabled = scrollLeft >= max - 1;

        if (this.dots) {
          const current = this.currentIndex();
          Array.from(this.dots.children).forEach((dot, index) =>
            dot.setAttribute('aria-current', String(index === current))
          );
        }

        if (this.thumb) {
          if (max <= 1) {
            this.thumb.style.width = '100%';
            this.thumb.style.transform = 'none';
            return;
          }
          const ratio = clientWidth / scrollWidth;
          this.thumb.style.width = `${ratio * 100}%`;
          this.thumb.style.transform = `translateX(${(scrollLeft / max) * (1 / ratio - 1) * 100}%)`;
        }
      }

      // Glisser à la souris (le doigt et le trackpad utilisent le défilement natif).
      initMouseDrag() {
        let startX = 0;
        let startScroll = 0;
        let pressed = false;
        let dragged = false;
        const onTrack = (event) => this.track && event.target.closest('[data-dx-track]') === this.track;

        this.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0 || !onTrack(event)) return;
          pressed = true;
          dragged = false;
          startX = event.clientX;
          startScroll = this.track.scrollLeft;
        });

        this.addEventListener('pointermove', (event) => {
          if (!pressed) return;
          const delta = event.clientX - startX;
          if (!dragged && Math.abs(delta) > 4) {
            dragged = true;
            this.track.setPointerCapture(event.pointerId);
            this.track.classList.add('is-dragging');
          }
          if (dragged) this.track.scrollLeft = startScroll - delta;
        });

        const release = () => {
          pressed = false;
          this.track?.classList.remove('is-dragging');
        };
        this.addEventListener('pointerup', release);
        this.addEventListener('pointercancel', release);

        // Un glisser ne doit pas déclencher de clic (lecture d'une vidéo, lien).
        this.addEventListener(
          'click',
          (event) => {
            if (!dragged) return;
            event.preventDefault();
            event.stopPropagation();
            dragged = false;
          },
          true
        );
      }
    }
  );
}
